import { useState } from "react";
import { trpc } from "../lib/trpc";
import { Button, Input } from "../components/ui";

/** Full-screen email + password sign-in. On success, refetch auth.me so the app gate re-renders. */
export function Login() {
  const utils = trpc.useUtils();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const login = trpc.auth.login.useMutation({ onSuccess: () => utils.auth.me.invalidate() });

  return (
    <div className="flex h-screen items-center justify-center bg-slate-50">
      <form
        onSubmit={(e) => { e.preventDefault(); login.mutate({ email, password }); }}
        className="w-full max-w-sm space-y-4 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
      >
        <div className="text-center">
          <h1 className="text-lg font-bold text-slate-900">PBS Integration</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to continue.</p>
        </div>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-600">Email</span>
          <Input type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} className="w-full" autoComplete="username" required />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-600">Password</span>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full" autoComplete="current-password" required />
        </label>
        {login.error && <p className="text-sm text-red-600">{login.error.message}</p>}
        <Button type="submit" disabled={login.isPending || !email || !password} className="w-full justify-center">
          {login.isPending ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </div>
  );
}

/**
 * Change-password form. `forced` (must-change after a temp password) hides the current-password field
 * and renders full-screen; otherwise it's the in-app "change password" form with a current-password check.
 */
export function ChangePassword({ forced, onDone }: { forced?: boolean; onDone?: () => void }) {
  const utils = trpc.useUtils();
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const set = trpc.auth.setOwnPassword.useMutation({
    onSuccess: () => { utils.auth.me.invalidate(); onDone?.(); },
  });
  const localErr = pw.length > 0 && pw.length < 8 ? "Use at least 8 characters." : pw2.length > 0 && pw !== pw2 ? "Passwords don't match." : null;
  const canSubmit = pw.length >= 8 && pw === pw2 && (forced || current.length > 0) && !set.isPending;

  const inner = (
    <form
      onSubmit={(e) => { e.preventDefault(); if (canSubmit) set.mutate({ currentPassword: forced ? undefined : current, newPassword: pw }); }}
      className="w-full max-w-sm space-y-4 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
    >
      <div>
        <h1 className="text-lg font-bold text-slate-900">{forced ? "Set a new password" : "Change password"}</h1>
        {forced && <p className="mt-1 text-sm text-slate-500">You signed in with a temporary password — choose your own to continue.</p>}
      </div>
      {!forced && (
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-600">Current password</span>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full" autoComplete="current-password" />
        </label>
      )}
      <label className="block text-sm">
        <span className="mb-1 block font-medium text-slate-600">New password</span>
        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full" autoComplete="new-password" />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium text-slate-600">Confirm new password</span>
        <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full" autoComplete="new-password" />
      </label>
      {(localErr || set.error) && <p className="text-sm text-red-600">{localErr ?? set.error?.message}</p>}
      <div className="flex gap-2">
        <Button type="submit" disabled={!canSubmit} className="justify-center">{set.isPending ? "Saving…" : "Save password"}</Button>
        {!forced && onDone && <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>}
      </div>
    </form>
  );

  if (forced) return <div className="flex h-screen items-center justify-center bg-slate-50">{inner}</div>;
  return inner;
}

/** Your own profile: change display name + login email (email change needs your current password). */
function ProfileCard() {
  const utils = trpc.useUtils();
  const me = trpc.auth.me.useQuery();
  const curEmail = (me.data?.email ?? "").toLowerCase();
  const [name, setName] = useState(me.data?.name ?? "");
  const [email, setEmail] = useState(me.data?.email ?? "");
  const [current, setCurrent] = useState("");
  const save = trpc.auth.setOwnProfile.useMutation({ onSuccess: () => { utils.auth.me.invalidate(); setCurrent(""); } });
  const emailChanged = email.trim().toLowerCase() !== curEmail;
  const nameChanged = (name.trim() || "") !== (me.data?.name ?? "");
  const canSave = (emailChanged || nameChanged) && (!emailChanged || current.length > 0) && !save.isPending;

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (canSave) save.mutate({ name: name.trim(), newEmail: emailChanged ? email.trim() : undefined, currentPassword: emailChanged ? current : undefined }); }}
      className="w-full space-y-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h2 className="font-bold text-slate-900">Account</h2>
      <label className="block text-sm">
        <span className="mb-1 block font-medium text-slate-600">Display name</span>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="w-full" placeholder="Your name" />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium text-slate-600">Email (used to sign in)</span>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full" autoComplete="username" />
      </label>
      {emailChanged && (
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-600">Current password <span className="font-normal text-slate-400">— required to change your email</span></span>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full" autoComplete="current-password" />
        </label>
      )}
      {save.error && <p className="text-sm text-red-600">{save.error.message}</p>}
      {save.isSuccess && !emailChanged && !nameChanged && <p className="text-sm text-emerald-600">Saved ✓</p>}
      <Button type="submit" disabled={!canSave} className="justify-center">{save.isPending ? "Saving…" : "Save profile"}</Button>
    </form>
  );
}

/** Full account modal: profile (name/email) + password change. */
export function AccountSettings({ onDone }: { onDone?: () => void }) {
  return (
    <div className="max-h-[90vh] w-full max-w-sm space-y-4 overflow-y-auto">
      <ProfileCard />
      <ChangePassword onDone={onDone} />
    </div>
  );
}
